import { CSSProperties, ElementType, FC, useMemo } from 'react';
import { Variant } from '@material-ui/core/styles/createTypography';
import { Link } from '@backstage/core-components';
import { Typography } from '@material-ui/core';
import { WbTooltip } from '../../WbTooltip';
import { WbTruncatedTypographyWrapper } from '../../WbTruncatedTypographyWrapper';

export type DefaultCellBaseProps = {
  typographyProps?: {
    color?:
      | 'inherit'
      | 'initial'
      | 'secondary'
      | 'primary'
      | 'textPrimary'
      | 'textSecondary'
      | 'error';
    component?: ElementType;
    variant?: Variant | 'srOnly' | 'inherit';
    style?: CSSProperties;
    className?: string;
  };
  tooltip?: boolean;
  link?: string;
};

export type DefaultCellProps = DefaultCellBaseProps & { value: any };

export const DefaultCell: FC<DefaultCellProps> = props => {
  const linkComponent = useMemo(() => {
    if (props.link) {
      return (
        <Link color="inherit" to={props.link}>
          {props.value}
        </Link>
      );
    }
    return props.value;
  }, [props.link, props.value]);

  const typographyComponent = useMemo(() => {
    if (props.typographyProps) {
      return (
        <Typography
          color={props.typographyProps.color}
          component={props.typographyProps.component || 'span'}
          variant={props.typographyProps.variant}
          style={props.typographyProps.style}
          className={props.typographyProps.className}
        >
          {linkComponent}
        </Typography>
      );
    }
    return linkComponent;
  }, [props.typographyProps, linkComponent]);

  const tooltipComponent = useMemo(() => {
    if (!!props.tooltip && typeof props.value === 'string') {
      const finalComponent = !props.typographyProps ? (
        <Typography>{typographyComponent}</Typography>
      ) : (
        typographyComponent
      );

      return (
        <div style={{ position: 'relative' }}>
          <WbTooltip
            title={props.tooltip ? props.value : ''}
            placement="bottom-start"
          >
            <div>{finalComponent}</div>
          </WbTooltip>
        </div>
      );
    }
    return null;
  }, [props.tooltip, props.typographyProps, props.value, typographyComponent]);

  const truncatedComponent = useMemo(() => {
    if (
      !props.tooltip &&
      typeof props.value === 'string' &&
      !props.link &&
      !props.typographyProps
    ) {
      return <WbTruncatedTypographyWrapper value={props.value} />;
    }
    return null;
  }, [props.tooltip, props.typographyProps, props.value, props.link]);

  if (tooltipComponent) {
    return tooltipComponent;
  }

  if (truncatedComponent) {
    return truncatedComponent;
  }

  return typographyComponent;
};
